import React from 'react';
import Layout from '../../components/layout/Layout';
import ServiceSelector from '../../components/interactive/ServiceSelector';
import { ServiceSelectorProvider } from '../../context/ServiceSelectorContext';

const ServiceSelectorPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section - Clean and Professional */}
      <div className="bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50"></div>
        <div className="relative py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
                AI Service Selector
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-600">
                Find your perfect AI solution in minutes
              </p>
              <div className="inline-flex items-center bg-indigo-50 border border-indigo-200 rounded-full px-6 py-3">
                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse mr-3"></div>
                <span className="text-indigo-700 font-medium">Intelligent Matching System</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selector Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <ServiceSelectorProvider>
            <ServiceSelector />
          </ServiceSelectorProvider>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {steps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Step Number */}
                  <div className="relative mb-8">
                    <div className="w-16 h-16 rounded-full bg-blue-100 mx-auto flex items-center justify-center text-blue-600 text-2xl font-bold">
                      {step.number}
                    </div>
                    {/* Connection Line (not for last item) */}
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-8 left-1/2 w-24 h-px bg-gray-300 transform translate-x-full"></div>
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="bg-gray-50 rounded-lg p-6 h-full">
                    <h3 className="text-xl font-bold mb-4 text-gray-900">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-blue-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Smart AI Matching Technology
                </h3>
                <p className="text-gray-600 mb-6">
                  Our advanced algorithm analyzes multiple factors to recommend the most suitable AI solution for your needs
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center bg-blue-50 rounded-lg p-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-gray-700">Industry-specific analysis</span>
                  </div>
                  <div className="flex items-center bg-blue-50 rounded-lg p-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-gray-700">Budget optimization</span>
                  </div>
                  <div className="flex items-center bg-blue-50 rounded-lg p-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-gray-700">ROI-focused recommendations</span>
                  </div>
                  <div className="flex items-center bg-blue-50 rounded-lg p-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-3"></div>
                    <span className="text-gray-700">Implementation timeline estimates</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const steps = [
  {
    number: "1",
    title: "Tell Us About Your Business",
    description: "Share information about your organization size, industry, current AI experience, and specific challenges you're facing."
  },
  {
    number: "2",
    title: "AI Analysis & Matching",
    description: "Our intelligent system analyzes your requirements against thousands of successful AI implementations to find the best fit."
  },
  {
    number: "3",
    title: "Get Your Personalized Plan",
    description: "Receive tailored recommendations with implementation timelines, cost estimates, and next steps to get started."
  }
];

export default ServiceSelectorPage;